/* eslint-disable import/no-cycle */
import NetworkContent from '@/helpers/networking/NetworkContent';
import usersService from '@/services/usersService';

export default {
  state: {
    teams: [],
    selectedTeams: [],
    teamSpace: [],
    assessments: [],
    notifications: [],
    overlayNotes: [],
    frontendOverlays: [],
    toastNotifications: [],
    refreshValue: Date.now(),
    profileDashboardDesc: null,
    devTippOverview: [],
    devTippLibrary: [],
    showDevTips: false,
    knowledgeDatabaseContent: [],
    kssHistory: null,
    quickAccessLinks: [],
    personalGoals: [],
    selectedCreatorTab: '',
    maxLimit: 3, // predefined max limit for personal goals
    activeGoalsCount: 0,
    goalUpdateObject: {},
    travelBuddyMeetingInfo: false,
    travelBuddyMeetingStatus: '',
    travelBuddyFeedbackText: '',
    travelBuddyEditFeedbackMode: false,
    travelBuddyHistory: [],
    newUserDemoContent: '',
    allExternalEvaluations: [],
    adminExternalEvaluations: [],
    reflexionGuidelineAssessment: '',
  },
  getters: {
    getTeams: (state) => state.teams,
    getTeamsForSelection: (state) => state.teams.filter((t) => t.isAdminTeam !== true),
    getNotifications: (state) => state.notifications,
    getToastNotifications: (state) => state.toastNotifications,
    getOverlayNotes: (state) => state.overlayNotes,
    getFrontendOverlays: (state) => state.frontendOverlays,
    getRefreshValue: (state) => state.refreshValue,
    getProfileDashboardDesc: (state) => state.profileDashboardDesc,
    getDevTippOverview: (state) => state.devTippOverview || [],
    getDevTippLibrary: (state) => state.devTippLibrary,
    getShowDevTipps: (state) => state.showDevTips,
    getKnowledgeDatabaseContent: (state) => state.knowledgeDatabaseContent,
    getHasNewDevTipps: (state) => (state.devTippOverview || [])
      .some((el) => Boolean(el?.new ?? el?.isNew)),
    visibleNoteOverlays: (state) => state.overlayNotes.length > 0,
    visibleFrontendOverlays: (state) => state.frontendOverlays.length > 0,
    getKssHistory: (state) => state.kssHistory,
    getQuickAccessLinks: (state) => state.quickAccessLinks,
    getPersonalGoals: (state) => state.personalGoals,
    getSelectedCreatorTab: (state) => state.selectedCreatorTab,
    getPersonalGoalsMaxLimit: (state) => state.maxLimit,
    getActiveGoalsCount: (state) => state.activeGoalsCount,
    getGoalUpdateObject: (state) => state.goalUpdateObject,
    getTravelBuddyMeetingInfo: (state) => state.travelBuddyMeetingInfo,
    getTravelBuddyMeetingStatus: (state) => state.travelBuddyMeetingStatus,
    getTravelBuddyFeedbackText: (state) => state.travelBuddyFeedbackText,
    getTravelBuddyEditFeedbackMode: (state) => state.travelBuddyEditFeedbackMode,
    getTravelBuddyHistory: (state) => state.travelBuddyHistory,
    getNewUserDemoContent: (state) => state.newUserDemoContent,
    getExternalEvaluations: (state) => state.allExternalEvaluations,
    getAdminExternalEvaluations: (state) => state.adminExternalEvaluations,
    getReflexionGuidelineAssessments: (state) => state.reflexionGuidelineAssessment,
    getAllAssessments: (state) => state.assessments,
  },
  mutations: {
    setRefreshValue: (state) => {
      state.refreshValue = Date.now();
    },
    setTeams: (state, teams) => {
      state.teams = [];
      if (Array.isArray(teams)) {
        teams.forEach((el) => {
          state.teams.push({
            _id: el._id,
            name: el.name,
            isChecked: false,
            isAdminTeam: el.isAdminTeam === true,
            members: Array.isArray(el.members) ? el.members.map((id) => String(id)) : [],
            leaders: Array.isArray(el.leaders) ? el.leaders.map((id) => String(id)) : [],
          });
        });
      }
    },
    resetTeams: (state) => {
      state.teams = [];
    },
    setSelectedTeams: (state, data) => {
      state.selectedTeams = data;
    },
    toggleTeamSelection: (state, teamId) => {
      const indexToDelete = state.selectedTeams.indexOf(teamId);
      if (indexToDelete > -1) {
        state.selectedTeams.splice(indexToDelete, 1);
      } else {
        state.selectedTeams.push(teamId);
      }
    },
    setProfileDashboardDesc: (state, data) => {
      state.profileDashboardDesc = data;
    },
    setTeamspace: (state, data) => {
      // Only update if we have valid data
      if (data && Array.isArray(data)) {
        state.teamSpace = data; // Replace the entire array instead of pushing
      }
    },
    setAssessments: (state, data) => {
      state.assessments = data;
    },
    setNotifications: (state, data) => {
      state.notifications = data;
    },
    setToastNotification: (state, data) => {
      state.toastNotifications.push(data);
    },
    setOverlayNotes(state, overlays) {
      state.overlayNotes = overlays;
    },
    setDevTippOverview: (state, data) => {
      state.devTippOverview = data;
    },
    setDevTippLibrary: (state, data) => {
      state.devTippLibrary = data;
    },
    setKnowledgeDatabaseContent: (state, data) => {
      state.knowledgeDatabaseContent = data;
    },
    setOverlayNote(state, data) {
      state.overlayNotes = data;
    },
    addFrontendOverlay(state, overlay) {
      state.frontendOverlays.push(overlay);
    },
    toggleShowDevTipps: (state) => {
      state.showDevTips = !state.showDevTips;
    },
    setShowDevTipps: (state, data) => {
      state.showDevTips = data;
    },
    removeFrontendOverlay(state, id) {
      const index = state.frontendOverlays.findIndex((el) => el.id === id);
      state.frontendOverlays.splice(index, 1);
    },
    removeTeams: (state) => {
      state.teams = [];
    },
    removeSelectedTeams: (state) => {
      state.selectedTeams = [];
    },
    removeTeamspace: (state) => {
      state.teamSpace = [];
    },
    removeAssessments: (state) => {
      state.assessments = [];
    },
    removeToastNotification: (state, id) => {
      const index = state.toastNotifications.findIndex((el) => el.id === id);
      state.toastNotifications.splice(index, 1);
    },
    setKssHistory: (state, data) => {
      state.kssHistory = data;
    },
    setQuickAccessLinks: (state, data) => {
      state.quickAccessLinks = data;
    },
    setPersonalGoals: (state, data) => {
      state.personalGoals = data;
    },
    setSelectedCreateTab: (state, data) => {
      state.selectedCreatorTab = data;
    },
    setPersonalGoalsMaxLimit: (state, data) => {
      state.maxLimit = data;
    },
    setActiveGoalsCount: (state, data) => {
      state.activeGoalsCount = data;
    },
    setGoalUpdateObject: (state, data) => {
      state.goalUpdateObject = data;
    },
    removeGoalsObject: (state) => {
      state.goalUpdateObject = {};
    },
    setTravelBuddyMeetingInfo: (state, data) => {
      state.travelBuddyMeetingInfo = data;
    },
    submitTravelBuddyFeedback: (state, data) => {
      state.travelBuddyFeedbackText = data?.feedback?.trim?.() || '';
    },
    setTravelBuddyMeetingStatus: (state, data) => {
      state.travelBuddyMeetingStatus = data;
    },
    setTravelBuddyEditFeedbackMode: (state, value) => {
      state.travelBuddyEditFeedbackMode = Boolean(value);
    },
    setTravelBuddyFeedbackText: (state, data) => {
      state.travelBuddyFeedbackText = data;
    },
    setTravelBuddyHistory: (state, data) => {
      state.travelBuddyHistory = data.allMeetings;
    },
    setNewUserDemoContent: (state, data) => {
      state.newUserDemoContent = data;
    },

    setExternalEvaluations: (state, data) => {
      state.allExternalEvaluations = data;
    },
    setAdminExternalEvaluations: (state, data) => {
      state.adminExternalEvaluations = data;
    },
    setReflexionGuidelineAssessments: (state, data) => {
      state.reflexionGuidelineAssessment = data;
    },
  },
  actions: {
    async getTeams({ commit }) {
      const { data } = await NetworkContent.getTeams();
      commit('setTeams', data.teams);
    },
    async resetTeams({ commit }) {
      commit('resetTeams');
    },
    async getShortUsers({ commit }) {
      try {
        const { data } = await NetworkContent.getShortUsers();
        if (data && data.data) {
          commit('setTeamspace', data.data);
        }
      } catch (error) {
        console.error('Error fetching short users:', error);
      }
    },
    async getAssessmentsOverview({ commit }) {
      const { data } = await NetworkContent.getAssessmentsOverview();
      commit('setAssessments', data);
    },
    async getNotifications({ commit }) {
      const { data } = await NetworkContent.getNotifications();
      commit('setNotifications', data.popups);
    },
    async getOverlayNotes({ commit }) {
      const { data } = await NetworkContent.getOverlayNotes();
      commit('setOverlayNotes', data.popups);
    },
    async deactivatePopUp({ commit }, id) {
      await NetworkContent.deactivatePopUp(id);
      const { data } = await NetworkContent.getNotifications();
      commit('setNotifications', data.popups);
    },
    async getDashboardProfileDescription({ commit }, type) {
      const config = {
        params: {
          type,
        },
      };
      const { data } = await NetworkContent.getDashboardProfileDesc(config);
      commit('setProfileDashboardDesc', data.data);
    },
    async deactivateOverlay({ commit }, id) {
      await NetworkContent.deactivateOverlay(id);
      const { data } = await NetworkContent.getOverlayNotes();
      commit('setOverlayNotes', data.popups);
    },
    async setTeams({ dispatch }, { id, teams }) {
      await NetworkContent.setTeams({ teams });
      dispatch('deactivateOverlay', id);
    },
    async setTags({ dispatch }, { id, tags }) {
      await NetworkContent.setTags({ tags });
      dispatch('deactivateOverlay', id);
    },
    async getDevTippOverview({ commit }) {
      try {
        const data = await usersService.getDevTipLibrary();
        console.log('DevTip API response:', data);
        commit('setDevTippOverview', data.data || []);
      } catch (error) {
        console.error('Error fetching DevTip overview:', error);
        commit('setDevTippOverview', []);
      }
    },
    async newUserDemoComplete({ commit, state }, { id }) {
      // Check if this is a local overlay (created by Demo button click) or backend overlay
      const isLocalOverlay = id && id.toString().startsWith('demo-');

      if (!isLocalOverlay) {
        // Backend overlay - call API to mark complete and deactivate
        await NetworkContent.newUserDemoComplete();
        await NetworkContent.deactivateOverlay(id);
      }

      // Remove overlay from frontend store
      const updatedOverlays = state.overlayNotes.filter((o) => o._id !== id);
      commit('setOverlayNotes', updatedOverlays);
    },
    async getDevTippLibrary({ commit }) {
      try {
        const data = await usersService.getDevTipLibrary();
        commit('setDevTippLibrary', data.data || []);
      } catch (error) {
        console.error('Error fetching DevTip library:', error);
        commit('setDevTippLibrary', []);
      }
    },

    async submitFeedback({ dispatch }, data) {
      await usersService.submitDevTipFeedback(data);
      dispatch('getDevTippOverview');
    },

    async getKnowledgeDatabase({ commit }) {
      const { data } = await NetworkContent.getKnowledgeDatabase();
      commit('setKnowledgeDatabaseContent', data.content);
    },
    async getKssHistory({ commit }) {
      const { data } = await NetworkContent.getKssHistory();
      commit('setKssHistory', data.data);
    },
    async getQuickAccessLinks({ commit }) {
      const { data } = await NetworkContent.getQuickAccessLinks();
      commit('setQuickAccessLinks', data.content);
    },
    async getPersonalGoals({ commit }) {
      const { data } = await NetworkContent.getPersonalGoals();
      commit('setPersonalGoals', data.personalGoals);
      commit('setPersonalGoalsMaxLimit', data.maxLimit);
    },
    async changeGoalStatus({ dispatch }, data) {
      await NetworkContent.changeGoalStatus(data);
      dispatch('getPersonalGoals');
    },
    async submitPersonalGoalFeedback({ dispatch }, data) {
      await NetworkContent.submitPersonalGoalFeedback(data);
      dispatch('getPersonalGoals');
    },
    async createPersonalGoal({ dispatch }, data) {
      await NetworkContent.createPersonalGoal(data);
      dispatch('getPersonalGoals');
    },
    async updatePersonalGoal({ dispatch }, data) {
      await NetworkContent.updatePersonalGoal(data);
      dispatch('getPersonalGoals');
    },
    async getTravelBuddyInfo({ commit }) {
      try {
        const { data } = await NetworkContent.getTravelBuddyMeetingInfo();
        const ownFeedbackEntry = data.myFeedbackEntry
          || data.meetingInfo?.meetingFeedback?.find(
            (entry) => String(entry.user) !== String(data.teammateID),
          )
          || null;

        commit('setTravelBuddyMeetingInfo', data);
        commit('setTravelBuddyMeetingStatus', data.meetingInfo?.meetingStatus ?? '');
        commit(
          'setTravelBuddyFeedbackText',
          ownFeedbackEntry?.feedback?.trim?.() || data.myFeedback || '',
        );
      } catch (error) {
        console.log(error);
      }
    },
    async submitTravelBuddyFeedback({ commit, state }, payload) {
      await NetworkContent.submitTravelBuddyFeedback(payload);

      const existingMeetingInfo = state.travelBuddyMeetingInfo || {};
      const existingMeetingData = existingMeetingInfo.meetingInfo || {};
      const meetingFeedback = Array.isArray(existingMeetingData.meetingFeedback)
        ? [...existingMeetingData.meetingFeedback]
        : [];

      const teammateId = existingMeetingInfo?.teammateID;
      const ownEntryIndex = meetingFeedback.findIndex(
        (entry) => String(entry.user) !== String(teammateId),
      );

      const normalizedOwnEntry = {
        ...(meetingFeedback[ownEntryIndex] || {}),
        feedback: payload.feedbackText?.trim?.() || '',
        helpfulnessScore: payload.helpfulnessScore,
        furtherThoughts: payload.furtherThoughts?.trim?.() || '',
        shareWithTravelBuddy: Boolean(payload.shareWithTravelBuddy),
        date: payload.meetingDate,
      };

      if (ownEntryIndex >= 0) {
        meetingFeedback[ownEntryIndex] = normalizedOwnEntry;
      } else if (normalizedOwnEntry.user) {
        meetingFeedback.push(normalizedOwnEntry);
      }

      const meetingStatusToSet = payload.editOnly ?
        existingMeetingData.meetingStatus : payload.meetingStatus;
      commit('setTravelBuddyMeetingInfo', {
        ...existingMeetingInfo,
        hasMyFeedback: Boolean(normalizedOwnEntry?.feedback?.trim?.()),
        myFeedback: normalizedOwnEntry.feedback,
        myFeedbackEntry: normalizedOwnEntry,
        meetingInfo: {
          ...existingMeetingData,
          meetingStatus: meetingStatusToSet,
          meetingFeedback,
        },
      });
      if (!payload.editOnly) {
        commit('setTravelBuddyMeetingStatus', payload.meetingStatus);
      }
      commit('submitTravelBuddyFeedback', normalizedOwnEntry);
    },
    async getTravelBuddyHistory({ commit }) {
      try {
        const { data } = await NetworkContent.getTravelBuddyHistory();
        commit('setTravelBuddyHistory', data);
      } catch (error) {
        console.log(error);
      }
    },
    async getSelectedTravelBuddyMeetingInfo({ commit }, payload) {
      try {
        const meetingId = typeof payload === 'string' ? payload : payload?.meetingId;
        const meetingStatusOverride = typeof payload === 'string'
          ? undefined
          : payload?.meetingStatusOverride;
        const { data } = await NetworkContent.getSelectedTravelBuddyMeetingInfo({ meetingId });
        const ownFeedbackEntry = data.myFeedbackEntry
          || data.meetingInfo?.meetingFeedback?.find(
            (entry) => String(entry.user) !== String(data.teammateID),
          )
          || null;
        commit('setTravelBuddyMeetingInfo', data);
        commit(
          'setTravelBuddyMeetingStatus',
          meetingStatusOverride ?? data.meetingInfo?.meetingStatus ?? '',
        );
        commit(
          'setTravelBuddyFeedbackText',
          ownFeedbackEntry?.feedback?.trim?.() || data.myFeedback || '',
        );
      } catch (error) {
        console.log(error);
      }
    },
    async getNewUserDemoContent({ commit }) {
      const { data } = await NetworkContent.getNewUserDemoContent();
      if (data.content.length) {
        const filteredContent = data.content.filter((item) => item.adminOnly === false);
        commit('setNewUserDemoContent', filteredContent);
      }
    },
    async getExternalEvaluation({ commit }) {
      try {
        const { data } = await NetworkContent.getExternalEvaluation();
        // Response contains externalEvaluationSeries grouped by seriesId
        commit('setExternalEvaluations', data.externalEvaluationSeries);
      } catch (error) {
        console.log(error);
      }
    },
    async sendExternalEvaluationReminder({ dispatch }, seriesId) {
      // Takes seriesId parameter
      await NetworkContent.sendExternalEvaluationReminder(seriesId);
      dispatch('getExternalEvaluation');
    },
    async extendExternalEvaluation({ dispatch }, seriesId) {
      // Takes seriesId parameter
      await NetworkContent.extendExternalEvaluation(seriesId);
      dispatch('getExternalEvaluation');
    },
    async updateExternalEvaluation({ dispatch }, { seriesId, data }) {
      // Takes seriesId and data (addUsers, addExternalUsers, removeAssessmentIds)
      await NetworkContent.updateExternalEvaluation(seriesId, data);
      await dispatch('getExternalEvaluation');
      await dispatch('getAdminExternalEvaluation');
    },
    async getAdminExternalEvaluation({ commit }) {
      try {
        const res = await NetworkContent.getAdminExternalEvaluations();
        commit('setAdminExternalEvaluations', res.data?.externalEvaluationSeries ?? []);
      } catch (error) {
        console.error('Error fetching admin external evaluations:', error);
      }
    },
    async createAdminExternalEvaluation({ dispatch }, payload) {
      await NetworkContent.createAdminExternalEvaluation(payload);
      await dispatch('getAdminExternalEvaluation');
    },
    async sendUserFeedback(_, data) {
      await NetworkContent.sendUserFeedback(data);
    },
    async showNewUserDemo({ commit, state }) {
      // Directly add overlay to frontend store for immediate display
      // This bypasses the async notification system for instant feedback
      const demoOverlay = {
        _id: `demo-${Date.now()}`,
        class: 'overlayNote',
        event: 'newUserDemo',
        date_sent: new Date(),
        date_begin: new Date(),
        date_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        active: true,
      };
      commit('setOverlayNotes', [demoOverlay, ...state.overlayNotes]);
    },
    async getReflexionGuidelineAssessments({ commit }) {
      const { data } = await NetworkContent.getReflexionGuidelineAssessments();
      commit('setReflexionGuidelineAssessments', data);
    },
  },
};
